import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { Appointment, AppointmentStatus, AppointmentType } from '@/types'
import type { AppointmentsState } from './state'

export interface CreateAppointmentData {
  organizationId?: string
  prospectId?: string
  requestedDateTime: Date
  type: AppointmentType
  title: string
  description?: string
  assignedTo?: string
  notes?: string
}

export interface UpdateAppointmentData {
  requestedDateTime?: Date
  proposedDateTimes?: Date[]
  confirmedDateTime?: Date
  status?: AppointmentStatus
  type?: AppointmentType
  title?: string
  description?: string
  meetingUrl?: string
  assignedTo?: string
  notes?: string
}

export const createActions = (state: AppointmentsState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapAppointmentDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): Appointment => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      organizationId: data.organizationId,
      prospectId: data.prospectId,
      requestedBy: data.requestedBy || '',
      requestedDateTime: (data.requestedDateTime as any)?.toDate?.() || new Date(),
      proposedDateTimes: ((data.proposedDateTimes || []) as any[]).map((d: any) => d?.toDate?.() || new Date()),
      confirmedDateTime: (data.confirmedDateTime as any)?.toDate?.(),
      status: data.status,
      type: data.type,
      title: data.title,
      description: data.description,
      meetingUrl: data.meetingUrl,
      assignedTo: data.assignedTo,
      notes: data.notes,
      createdBy: data.createdBy || '',
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      updatedBy: data.updatedBy || '',
    } as Appointment
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'appointments'), ...baseConstraints, orderBy('requestedDateTime', 'desc'))

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'appointments'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('requestedDateTime', 'desc'))
      }
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let appointments = snapshot.docs.map(mapAppointmentDoc)
      // Client-side filter for operators
      if (authStore.user?.role === 'operator') {
        const orgIds = new Set(authStore.user.assignedOrganizationIds || [])
        appointments = appointments.filter((a) => a.organizationId && orgIds.has(a.organizationId))
      }
      state.appointments = appointments
      state.isLoading = false
    }, (error) => {
      console.error('Appointments subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchAppointments = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, 'appointments'), ...baseConstraints, orderBy('requestedDateTime', 'desc'))

      // Filter by organization for clients
      if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'appointments'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('requestedDateTime', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        const orgIds = authStore.user?.assignedOrganizationIds || []
        if (orgIds.length === 0) {
          state.appointments = []
          state.isLoading = false
          return
        }
        // Fetch in batches of 30
        const allAppointments: Appointment[] = []
        for (let i = 0; i < orgIds.length; i += 30) {
          const batch = orgIds.slice(i, i + 30)
          const batchQ = query(
            collection(db, 'appointments'),
            ...baseConstraints,
            where('organizationId', 'in', batch),
            orderBy('requestedDateTime', 'desc')
          )
          const snap = await getDocs(batchQ)
          allAppointments.push(...snap.docs.map((docSnap) => {
            const data = docSnap.data()
            return {
              id: docSnap.id,
              tenantId: data.tenantId,
              organizationId: data.organizationId,
              prospectId: data.prospectId,
              requestedBy: data.requestedBy || '',
              requestedDateTime: data.requestedDateTime?.toDate() || new Date(),
              proposedDateTimes: data.proposedDateTimes?.map((d: { toDate: () => Date }) => d.toDate()) || [],
              confirmedDateTime: data.confirmedDateTime?.toDate(),
              status: data.status,
              type: data.type,
              title: data.title,
              description: data.description,
              meetingUrl: data.meetingUrl,
              assignedTo: data.assignedTo,
              notes: data.notes,
              createdBy: data.createdBy || '',
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              updatedBy: data.updatedBy || '',
            } as Appointment
          }))
        }
        state.appointments = allAppointments.sort((a, b) => b.requestedDateTime.getTime() - a.requestedDateTime.getTime())
        state.isLoading = false
        return
      }

      const snapshot = await getDocs(q)

      state.appointments = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          tenantId: data.tenantId,
          organizationId: data.organizationId,
          prospectId: data.prospectId,
          requestedBy: data.requestedBy || '',
          requestedDateTime: data.requestedDateTime?.toDate() || new Date(),
          proposedDateTimes: data.proposedDateTimes?.map((d: { toDate: () => Date }) => d.toDate()) || [],
          confirmedDateTime: data.confirmedDateTime?.toDate(),
          status: data.status,
          type: data.type,
          title: data.title,
          description: data.description,
          meetingUrl: data.meetingUrl,
          assignedTo: data.assignedTo,
          notes: data.notes,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy || '',
        } as Appointment
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching appointments'
      console.error('Error fetching appointments:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchAppointmentById = async (id: string): Promise<Appointment | null> => {
    try {
      const docRef = doc(db, 'appointments', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        organizationId: data.organizationId,
        prospectId: data.prospectId,
        requestedBy: data.requestedBy || '',
        requestedDateTime: data.requestedDateTime?.toDate() || new Date(),
        proposedDateTimes: data.proposedDateTimes?.map((d: { toDate: () => Date }) => d.toDate()) || [],
        confirmedDateTime: data.confirmedDateTime?.toDate(),
        status: data.status,
        type: data.type,
        title: data.title,
        description: data.description,
        meetingUrl: data.meetingUrl,
        assignedTo: data.assignedTo,
        notes: data.notes,
        createdBy: data.createdBy || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || '',
      } as Appointment
    } catch (error) {
      console.error('Error fetching appointment:', error)
      return null
    }
  }

  const createAppointment = async (data: CreateAppointmentData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    // Use provided organizationId or fall back to user's organizationId
    const organizationId = data.organizationId || authStore.user?.organizationId || null

    const appointmentData = {
      ...getTenantData(),
      organizationId,
      prospectId: data.prospectId || null,
      requestedBy: userId,
      requestedDateTime: data.requestedDateTime,
      status: 'requested' as AppointmentStatus,
      type: data.type,
      title: data.title,
      description: data.description || null,
      assignedTo: data.assignedTo || null,
      notes: data.notes || null,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'appointments'), appointmentData)

    const newAppointment: Appointment = {
      id: docRef.id,
      organizationId: organizationId || undefined,
      prospectId: data.prospectId,
      requestedBy: userId,
      requestedDateTime: data.requestedDateTime,
      status: 'requested',
      type: data.type,
      title: data.title,
      description: data.description,
      assignedTo: data.assignedTo,
      notes: data.notes,
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.appointments.unshift(newAppointment)

    await logCreate('appointment', docRef.id, data.title)

    // Notify admins and operators about new appointment request
    await notificationsStore.notifyAdminsAndOperators(
      'appointment_created',
      'Nueva cita solicitada',
      `Se ha solicitado una cita: "${data.title}"`,
      'appointment',
      docRef.id
    )

    return docRef.id
  }

  const updateAppointment = async (id: string, data: UpdateAppointmentData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'appointments', id)
    const existingAppointment = state.appointments.find((a) => a.id === id)

    const updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (data.requestedDateTime !== undefined) updateData.requestedDateTime = data.requestedDateTime
    if (data.proposedDateTimes !== undefined) updateData.proposedDateTimes = data.proposedDateTimes
    if (data.confirmedDateTime !== undefined) updateData.confirmedDateTime = data.confirmedDateTime
    if (data.status !== undefined) updateData.status = data.status
    if (data.type !== undefined) updateData.type = data.type
    if (data.title !== undefined) updateData.title = data.title
    if (data.description !== undefined) updateData.description = data.description
    if (data.meetingUrl !== undefined) updateData.meetingUrl = data.meetingUrl
    if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo
    if (data.notes !== undefined) updateData.notes = data.notes

    await updateDoc(docRef, updateData)

    const index = state.appointments.findIndex((a) => a.id === id)
    if (index !== -1) {
      state.appointments[index] = {
        ...state.appointments[index],
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingAppointment) {
      const fieldsToTrack: (keyof Appointment)[] = ['status', 'title', 'confirmedDateTime']
      const changes = detectChanges(
        existingAppointment as unknown as Record<string, unknown>,
        data as Record<string, unknown>,
        fieldsToTrack as string[]
      )
      if (changes.length > 0) {
        await logUpdate('appointment', id, changes, existingAppointment.title)
      }
    }
  }

  const updateAppointmentStatus = async (id: string, status: AppointmentStatus): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'appointments', id)
    const existingAppointment = state.appointments.find((a) => a.id === id)

    await updateDoc(docRef, {
      status,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    })

    const index = state.appointments.findIndex((a) => a.id === id)
    if (index !== -1) {
      state.appointments[index] = {
        ...state.appointments[index],
        status,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingAppointment) {
      await logUpdate('appointment', id, [
        { field: 'status', oldValue: existingAppointment.status, newValue: status },
      ], existingAppointment.title)
    }
  }

  const proposeTimesForAppointment = async (id: string, proposedDateTimes: Date[]): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'appointments', id)

    await updateDoc(docRef, {
      proposedDateTimes,
      status: 'proposed' as AppointmentStatus,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    })

    const index = state.appointments.findIndex((a) => a.id === id)
    if (index !== -1) {
      state.appointments[index] = {
        ...state.appointments[index],
        proposedDateTimes,
        status: 'proposed',
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    await logUpdate('appointment', id, [
      { field: 'status', oldValue: 'requested', newValue: 'proposed' },
    ])
  }

  const confirmAppointment = async (id: string, confirmedDateTime: Date, meetingUrl?: string): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'appointments', id)

    const updateData: Record<string, unknown> = {
      confirmedDateTime,
      status: 'confirmed' as AppointmentStatus,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (meetingUrl) {
      updateData.meetingUrl = meetingUrl
    }

    await updateDoc(docRef, updateData)

    const index = state.appointments.findIndex((a) => a.id === id)
    if (index !== -1) {
      state.appointments[index] = {
        ...state.appointments[index],
        confirmedDateTime,
        meetingUrl: meetingUrl || state.appointments[index].meetingUrl,
        status: 'confirmed',
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    await logUpdate('appointment', id, [
      { field: 'status', oldValue: 'proposed', newValue: 'confirmed' },
    ])

    // Notify about confirmation
    const appointment = state.appointments.find((a) => a.id === id)
    if (appointment) {
      const recipients: string[] = []
      if (appointment.requestedBy && appointment.requestedBy !== authStore.user?.id) {
        recipients.push(appointment.requestedBy)
      }
      if (appointment.assignedTo && appointment.assignedTo !== authStore.user?.id) {
        recipients.push(appointment.assignedTo)
      }
      if (recipients.length > 0) {
        await notificationsStore.notifyUsers(
          recipients,
          'appointment_confirmed',
          'Cita confirmada',
          `La cita "${appointment.title}" ha sido confirmada`,
          'appointment',
          id
        )
      }
    }
  }

  const deleteAppointment = async (id: string): Promise<void> => {
    const appointment = state.appointments.find((a) => a.id === id)

    await deleteDoc(doc(db, 'appointments', id))

    state.appointments = state.appointments.filter((a) => a.id !== id)

    if (appointment) {
      await logDelete('appointment', id, appointment.title)
    }
  }

  const setFilters = (filters: Partial<AppointmentsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const getAppointmentById = (id: string): Appointment | undefined => {
    return state.appointments.find((a) => a.id === id)
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchAppointments,
    fetchAppointmentById,
    createAppointment,
    updateAppointment,
    updateAppointmentStatus,
    proposeTimesForAppointment,
    confirmAppointment,
    deleteAppointment,
    setFilters,
    getAppointmentById,
  }
}
